// Hook: GET /api/practice-areas/:id → PracticeAreaTemplate.
//
// The MatterDashboard renderer calls this with `matter.practiceAreaTemplateId`.
// Cached aggressively per template id — templates are immutable (or close
// to it) at runtime, and a single matter view often re-renders dozens of
// times as the user toggles deadlines / docs.

import { useQuery } from "@tanstack/react-query";
import { getAuthHeaders } from "../api/auth";

export interface WidgetConfig {
  type: string;
  key?: string;
  title?: string;
  source?: string;
  /** Declares the arithmetic behind a date. No widget computes it today. */
  compute?: string;
  /** Case-insensitive substrings of the real deadline labels a card is about.
      This, not `compute` or `key`, is what selects the row a card shows. */
  match?: string[];
  severity?: "critical" | "warning" | "info";
  stages?: string[];
  items?: string[];
  colSpan?: number;
  // Free-form — each widget reads its own keys from this bag.
  [k: string]: unknown;
}

export interface PracticeAreaTemplate {
  id: string;
  name: string;
  parent?: string;
  icon?: string;
  ui?: {
    widgets?: WidgetConfig[];
    stations?: string[];
    parties?: string[];
    status_terminology?: Record<string, string>;
  };
  workflow?: {
    key_deadlines?: Array<{
      name: string;
      compute?: string;
      severity?: string;
    }>;
    document_checklist?: Array<{
      id: string;
      label: string;
      required?: boolean;
    }>;
    [k: string]: unknown;
  };
  ai?: {
    classifier_keywords?: string[];
    prompt_set?: string;
    suggested_actions?: string[];
  };
}

export async function fetchPracticeAreaTemplate(id: string): Promise<PracticeAreaTemplate | null> {
  const res = await fetch(`/api/practice-areas/${encodeURIComponent(id)}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { template: PracticeAreaTemplate };
  return body.template ?? null;
}

export function usePracticeAreaTemplate(id: string | null | undefined) {
  return useQuery<PracticeAreaTemplate | null>({
    queryKey: ["practice-area-template", id ?? ""],
    queryFn: () => (id ? fetchPracticeAreaTemplate(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 min — template config rarely changes mid-session.
  });
}
